import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, writeBatch } from 'firebase/firestore';
import { db } from '../../services/firebase';
import * as XLSX from 'xlsx';

interface BookInventoryProps {
  domain: string;
}

interface Book {
  id: string;
  title: string;
  author: string;
  isbn: string;
  category: string;
  publisher: string;
  edition: string;
  year: string;
  totalCopies: number;
  availableCopies: number;
  shelfLocation: string;
  language: string;
  description: string;
  addedOn: string;
}

const categories = [
  'Fiction', 'Non-Fiction', 'Science', 'Mathematics', 'History',
  'Geography', 'English Literature', 'Hindi Literature', 'Computer Science',
  'Physics', 'Chemistry', 'Biology', 'Social Studies', 'Arts & Crafts',
  'Reference', 'Encyclopedia', 'Dictionary', 'Magazine', 'Newspaper',
  'General Knowledge', 'Moral Science', 'Other',
];

const languages = ['English', 'Hindi', 'Sanskrit', 'Urdu', 'Other'];

const BookInventory: React.FC<BookInventoryProps> = ({ domain }) => {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBook, setEditingBook] = useState<Book | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const emptyForm = {
    title: '',
    author: '',
    isbn: '',
    category: '',
    publisher: '',
    edition: '',
    year: '',
    totalCopies: 1,
    shelfLocation: '',
    language: 'English',
    description: '',
  };
  const [formData, setFormData] = useState(emptyForm);

  // Bulk upload state
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [parsedBooks, setParsedBooks] = useState<any[]>([]);
  const [bulkErrors, setBulkErrors] = useState<{ row: number; message: string }[]>([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkComplete, setBulkComplete] = useState(false);

  const downloadTemplate = () => {
    const templateData = [
      {
        'Title': 'The Great Gatsby',
        'Author': 'F. Scott Fitzgerald',
        'ISBN': '978-0-7432-7356-5',
        'Category': 'Fiction',
        'Publisher': 'Scribner',
        'Edition': '1st Edition',
        'Year': '1925',
        'Total Copies': 3,
        'Shelf Location': 'A-1-05',
        'Language': 'English',
        'Description': 'A classic novel of the Jazz Age',
      },
      {
        'Title': 'NCERT Mathematics Class 10',
        'Author': 'NCERT',
        'ISBN': '978-81-7450-636-3',
        'Category': 'Mathematics',
        'Publisher': 'NCERT',
        'Edition': '2024 Edition',
        'Year': '2024',
        'Total Copies': 25,
        'Shelf Location': 'B-2-01',
        'Language': 'English',
        'Description': 'CBSE standard textbook for Class 10',
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Books');

    ws['!cols'] = [
      { wch: 35 }, // Title
      { wch: 25 }, // Author
      { wch: 20 }, // ISBN
      { wch: 20 }, // Category
      { wch: 20 }, // Publisher
      { wch: 15 }, // Edition
      { wch: 8 },  // Year
      { wch: 14 }, // Total Copies
      { wch: 15 }, // Shelf Location
      { wch: 12 }, // Language
      { wch: 40 }, // Description
    ];

    XLSX.writeFile(wb, 'library_books_upload_template.xlsx');
  };

  const handleBulkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setBulkFile(selectedFile);
      setParsedBooks([]);
      setBulkErrors([]);
      setBulkComplete(false);
      parseBulkExcel(selectedFile);
    }
  };

  const parseBulkExcel = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const parsed: any[] = [];
        const errors: { row: number; message: string }[] = [];

        jsonData.forEach((row: any, index: number) => {
          const title = String(row['Title'] || '').trim();
          const author = String(row['Author'] || '').trim();
          const isbn = String(row['ISBN'] || '').trim();
          const category = String(row['Category'] || '').trim();
          const publisher = String(row['Publisher'] || '').trim();
          const edition = String(row['Edition'] || '').trim();
          const year = String(row['Year'] || '').trim();
          const totalCopies = parseInt(row['Total Copies']) || 1;
          const shelfLocation = String(row['Shelf Location'] || '').trim();
          const language = String(row['Language'] || 'English').trim();
          const description = String(row['Description'] || '').trim();

          if (!title) errors.push({ row: index + 2, message: 'Title is required' });
          if (!author) errors.push({ row: index + 2, message: 'Author is required' });
          if (!category) errors.push({ row: index + 2, message: 'Category is required' });
          if (totalCopies < 1) errors.push({ row: index + 2, message: 'Total Copies must be at least 1' });

          if (title && author && category && totalCopies >= 1) {
            parsed.push({
              title, author, isbn, category, publisher, edition,
              year, totalCopies, shelfLocation, language, description,
            });
          }
        });

        setParsedBooks(parsed);
        setBulkErrors(errors);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        setBulkErrors([{ row: 0, message: 'Failed to parse Excel file. Please check the format.' }]);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const uploadBulkBooks = async () => {
    if (bulkErrors.length > 0 || parsedBooks.length === 0) return;

    setBulkUploading(true);
    setBulkProgress(0);

    try {
      const batchSize = 500;
      const batches = [];
      for (let i = 0; i < parsedBooks.length; i += batchSize) {
        batches.push(parsedBooks.slice(i, i + batchSize));
      }

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = writeBatch(db);
        const currentBatch = batches[batchIndex];

        currentBatch.forEach((book) => {
          const docRef = doc(collection(db, `school-data/${domain}/library-books`));
          batch.set(docRef, {
            ...book,
            availableCopies: book.totalCopies,
            addedOn: new Date().toISOString(),
          });
        });

        await batch.commit();
        setBulkProgress(((batchIndex + 1) / batches.length) * 100);
      }

      setBulkComplete(true);
    } catch (error: any) {
      console.error('Error uploading books:', error);
      alert(`Failed to upload books: ${error.message}`);
    } finally {
      setBulkUploading(false);
    }
  };

  const resetBulkUpload = () => {
    setBulkFile(null);
    setParsedBooks([]);
    setBulkErrors([]);
    setBulkComplete(false);
    setBulkProgress(0);
  };

  useEffect(() => {
    if (!domain) return;

    const booksRef = collection(db, `school-data/${domain}/library-books`);
    const q = query(booksRef, orderBy('title', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const booksList: Book[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        booksList.push({
          id: docSnap.id,
          title: data.title || '',
          author: data.author || '',
          isbn: data.isbn || '',
          category: data.category || '',
          publisher: data.publisher || '',
          edition: data.edition || '',
          year: data.year || '',
          totalCopies: data.totalCopies || 0,
          availableCopies: data.availableCopies ?? data.totalCopies ?? 0,
          shelfLocation: data.shelfLocation || '',
          language: data.language || 'English',
          description: data.description || '',
          addedOn: data.addedOn || '',
        });
      });
      setBooks(booksList);
      setLoading(false);
    }, (error) => {
      console.error('Error loading books:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [domain]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: name === 'totalCopies' ? parseInt(value) || 0 : value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.author || !formData.category) {
      alert('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (editingBook) {
        const bookRef = doc(db, `school-data/${domain}/library-books`, editingBook.id);
        const copiesDiff = formData.totalCopies - editingBook.totalCopies;
        const newAvailable = Math.max(0, editingBook.availableCopies + copiesDiff);
        await updateDoc(bookRef, {
          ...formData,
          availableCopies: newAvailable,
          updatedOn: new Date().toISOString(),
        });
      } else {
        await addDoc(collection(db, `school-data/${domain}/library-books`), {
          ...formData,
          availableCopies: formData.totalCopies,
          addedOn: new Date().toISOString(),
        });
      }
      closeModal();
    } catch (error: any) {
      console.error('Error saving book:', error);
      alert(`Failed to save book: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (book: Book) => {
    if (!confirm(`Delete "${book.title}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, `school-data/${domain}/library-books`, book.id));
    } catch (error: any) {
      alert(`Failed to delete: ${error.message}`);
    }
  };

  const openEdit = (book: Book) => {
    setEditingBook(book);
    setFormData({
      title: book.title,
      author: book.author,
      isbn: book.isbn,
      category: book.category,
      publisher: book.publisher,
      edition: book.edition,
      year: book.year,
      totalCopies: book.totalCopies,
      shelfLocation: book.shelfLocation,
      language: book.language,
      description: book.description,
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingBook(null);
    setFormData(emptyForm);
  };

  const filtered = books.filter((b) => {
    const matchesSearch = !search ||
      b.title.toLowerCase().includes(search.toLowerCase()) ||
      b.author.toLowerCase().includes(search.toLowerCase()) ||
      b.isbn.toLowerCase().includes(search.toLowerCase()) ||
      b.publisher.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || b.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const totalBooks = books.reduce((sum, b) => sum + b.totalCopies, 0);
  const totalAvailable = books.reduce((sum, b) => sum + b.availableCopies, 0);
  const totalIssued = totalBooks - totalAvailable;
  const uniqueTitles = books.length;

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2 text-muted">Loading book inventory...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-book fs-3 text-primary"></i>
              <h4 className="mb-0 mt-1">{uniqueTitles}</h4>
              <small className="text-muted">Unique Titles</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-collection fs-3 text-info"></i>
              <h4 className="mb-0 mt-1">{totalBooks}</h4>
              <small className="text-muted">Total Copies</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-check-circle fs-3 text-success"></i>
              <h4 className="mb-0 mt-1">{totalAvailable}</h4>
              <small className="text-muted">Available</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-arrow-left-right fs-3 text-warning"></i>
              <h4 className="mb-0 mt-1">{totalIssued}</h4>
              <small className="text-muted">Issued</small>
            </div>
          </div>
        </div>
      </div>

      {/* Actions & Filters */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">
            <i className="bi bi-book me-2 text-primary-custom"></i>
            Book Inventory
          </h5>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary btn-sm" onClick={() => setShowBulkUpload(true)}>
              <i className="bi bi-cloud-upload me-2"></i>Bulk Upload
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)}>
              <i className="bi bi-plus-circle me-2"></i>Add Book
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search by title, author, ISBN, publisher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select className="form-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="all">All Categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3 text-end">
              <span className="text-muted small">{filtered.length} of {books.length} books</span>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 d-block mb-2"></i>
              {books.length === 0 ? 'No books in inventory. Add your first book!' : 'No books match your search.'}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Title</th>
                    <th>Author</th>
                    <th>Category</th>
                    <th>ISBN</th>
                    <th className="text-center">Total</th>
                    <th className="text-center">Available</th>
                    <th>Shelf</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((book) => (
                    <tr key={book.id}>
                      <td>
                        <strong>{book.title}</strong>
                        {book.edition && <small className="text-muted d-block">{book.edition}</small>}
                      </td>
                      <td>{book.author}</td>
                      <td><span className="badge bg-secondary">{book.category}</span></td>
                      <td><small className="text-muted">{book.isbn || '-'}</small></td>
                      <td className="text-center">{book.totalCopies}</td>
                      <td className="text-center">
                        <span className={`badge ${book.availableCopies > 0 ? 'bg-success' : 'bg-danger'}`}>
                          {book.availableCopies}
                        </span>
                      </td>
                      <td><small>{book.shelfLocation || '-'}</small></td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => openEdit(book)} title="Edit">
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(book)} title="Delete">
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-cloud-upload me-2"></i>
                  Bulk Upload Books
                </h5>
                <button type="button" className="btn-close" onClick={() => { setShowBulkUpload(false); resetBulkUpload(); }}></button>
              </div>
              <div className="modal-body">
                {/* Step 1: Download Template */}
                <div className="alert alert-info d-flex align-items-center mb-4">
                  <i className="bi bi-info-circle fs-4 me-3"></i>
                  <div className="flex-grow-1">
                    <strong>Step 1:</strong> Download the Excel template, fill in book details, and upload.
                    <br />
                    <small>Template includes sample data to guide you.</small>
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={downloadTemplate}>
                    <i className="bi bi-download me-2"></i>
                    Download Template
                  </button>
                </div>

                {/* Required Fields */}
                <div className="alert alert-warning mb-4">
                  <h6 className="alert-heading mb-1">
                    <i className="bi bi-lightbulb me-2"></i>
                    Required Fields
                  </h6>
                  <ul className="mb-0 small">
                    <li><strong>Title:</strong> Book title</li>
                    <li><strong>Author:</strong> Author name</li>
                    <li><strong>Category:</strong> {categories.join(', ')}</li>
                    <li><strong>Total Copies:</strong> Number of copies (minimum 1)</li>
                    <li><strong>Optional:</strong> ISBN, Publisher, Edition, Year, Shelf Location, Language, Description</li>
                  </ul>
                </div>

                {/* Step 2: Upload File */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-file-earmark-excel me-2"></i>
                    Step 2: Upload Excel File
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".xlsx,.xls"
                    onChange={handleBulkFileChange}
                    disabled={bulkUploading}
                  />
                  {bulkFile && (
                    <small className="text-muted d-block mt-2">
                      <i className="bi bi-check-circle text-success me-1"></i>
                      Selected: {bulkFile.name}
                    </small>
                  )}
                </div>

                {/* Validation Errors */}
                {bulkErrors.length > 0 && (
                  <div className="alert alert-danger">
                    <h6 className="alert-heading">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Validation Errors ({bulkErrors.length})
                    </h6>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr><th>Row</th><th>Error</th></tr>
                        </thead>
                        <tbody>
                          {bulkErrors.map((err, idx) => (
                            <tr key={idx}><td>{err.row}</td><td>{err.message}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Preview */}
                {parsedBooks.length > 0 && bulkErrors.length === 0 && (
                  <div className="mb-4">
                    <h6 className="fw-semibold mb-3">
                      <i className="bi bi-eye me-2 text-success"></i>
                      Preview ({parsedBooks.length} books)
                    </h6>
                    <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                      <table className="table table-sm table-bordered">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>Title</th>
                            <th>Author</th>
                            <th>ISBN</th>
                            <th>Category</th>
                            <th>Publisher</th>
                            <th>Copies</th>
                            <th>Shelf</th>
                            <th>Language</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsedBooks.map((book, idx) => (
                            <tr key={idx}>
                              <td>{book.title}</td>
                              <td>{book.author}</td>
                              <td><small>{book.isbn || '-'}</small></td>
                              <td><span className="badge bg-secondary">{book.category}</span></td>
                              <td><small>{book.publisher || '-'}</small></td>
                              <td className="text-center">{book.totalCopies}</td>
                              <td><small>{book.shelfLocation || '-'}</small></td>
                              <td><small>{book.language}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Upload Progress */}
                {bulkUploading && (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <span>Uploading books...</span>
                      <span>{Math.round(bulkProgress)}%</span>
                    </div>
                    <div className="progress" style={{ height: '25px' }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style={{ width: `${bulkProgress}%` }}
                      >
                        {Math.round(bulkProgress)}%
                      </div>
                    </div>
                  </div>
                )}

                {/* Success */}
                {bulkComplete && (
                  <div className="alert alert-success">
                    <i className="bi bi-check-circle me-2"></i>
                    Successfully uploaded {parsedBooks.length} books to library inventory!
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {!bulkComplete && parsedBooks.length > 0 && bulkErrors.length === 0 && (
                  <button className="btn btn-success" onClick={uploadBulkBooks} disabled={bulkUploading}>
                    {bulkUploading ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>Uploading...</>
                    ) : (
                      <><i className="bi bi-cloud-upload me-2"></i>Upload {parsedBooks.length} Books</>
                    )}
                  </button>
                )}
                {(parsedBooks.length > 0 || bulkComplete) && (
                  <button className="btn btn-outline-secondary" onClick={resetBulkUpload} disabled={bulkUploading}>
                    <i className="bi bi-arrow-counterclockwise me-2"></i>Reset
                  </button>
                )}
                <button className="btn btn-secondary" onClick={() => { setShowBulkUpload(false); resetBulkUpload(); }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-book me-2"></i>
                  {editingBook ? 'Edit Book' : 'Add New Book'}
                </h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-8">
                      <label className="form-label">Book Title *</label>
                      <input type="text" className="form-control" name="title" value={formData.title} onChange={handleChange} required />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">ISBN</label>
                      <input type="text" className="form-control" name="isbn" value={formData.isbn} onChange={handleChange} placeholder="e.g., 978-0-123456-78-9" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Author *</label>
                      <input type="text" className="form-control" name="author" value={formData.author} onChange={handleChange} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Publisher</label>
                      <input type="text" className="form-control" name="publisher" value={formData.publisher} onChange={handleChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Category *</label>
                      <select className="form-select" name="category" value={formData.category} onChange={handleChange} required>
                        <option value="">Select Category</option>
                        {categories.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Language</label>
                      <select className="form-select" name="language" value={formData.language} onChange={handleChange}>
                        {languages.map((l) => (
                          <option key={l} value={l}>{l}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Edition</label>
                      <input type="text" className="form-control" name="edition" value={formData.edition} onChange={handleChange} placeholder="e.g., 3rd Edition" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Year of Publication</label>
                      <input type="text" className="form-control" name="year" value={formData.year} onChange={handleChange} placeholder="e.g., 2023" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Total Copies *</label>
                      <input type="number" className="form-control" name="totalCopies" value={formData.totalCopies} onChange={handleChange} min={1} required />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Shelf Location</label>
                      <input type="text" className="form-control" name="shelfLocation" value={formData.shelfLocation} onChange={handleChange} placeholder="e.g., A-3-12" />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Description</label>
                      <textarea className="form-control" name="description" rows={2} value={formData.description} onChange={handleChange}></textarea>
                    </div>
                  </div>
                  <div className="mt-4 d-flex gap-2">
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? (
                        <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
                      ) : (
                        <><i className="bi bi-check-circle me-2"></i>{editingBook ? 'Update Book' : 'Add Book'}</>
                      )}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookInventory;
